/**
 * Copia REDUCIDA de un material para embeber en lineas: recetas de menu (ingredientes y
 * desechables), semielaborados y recetas de muestra (ingredientes, sustituciones, salidas),
 * opciones de modificador, ordenes de compra y movimientos (fix/export).
 *
 * Antes se embebia el doc completo del material: un semielaborado arrastraba su `recipe[]` con
 * mas copias anidadas, `intructions`, `stock_config`, `outputs`..., y una receta de menu llegaba a
 * ~90 KB. Nadie lee esos campos desde la copia de una linea (auditoria en el handoff de
 * fix/export): lo que haga falta del catalogo se relee por `$key`.
 *
 * `metric` y `measure` son la unidad en la que esta guardada la `quantity` de la linea (regla 26)
 * y la que usa el POS al descontar: al re-guardar una linea existente se conservan los de su copia
 * (`frozenFrom`). Solo quien recalcula `quantity` en otra unidad (el cambio de unidad o de medida)
 * puede pasar otros. Los demas campos se toman frescos de `material`.
 *
 * Sin dependencias a proposito. Nunca devuelve `undefined` en un campo (Firestore lo rechaza).
 */
export function slimMaterialSnapshot(
    material: any,
    options: { frozenFrom?: any; extraFields?: string[] } = {}
): any {
    const source = material || {};
    const unitSource = options.frozenFrom?.metric ? options.frozenFrom : source;
    const category = source.category
        ? { $key: source.category.$key ?? source.category_key ?? '', name: source.category.name ?? '' }
        : null;

    const snapshot: any = {
        $key: source.$key ?? source.material_key ?? '',
        name: source.name ?? '',
        sku: source.sku ?? '',
        type: source.type ?? null,
        measure: unitSource.measure ?? source.measure ?? null,
        metric: unitSource.metric
            ? { name: unitSource.metric.name ?? '', symbol: unitSource.metric.symbol ?? '' }
            : null,
        // Ausente = no descuenta inventario en el POS (`=== 'inventory_usage'`): null conserva eso.
        movement_type: source.movement_type ?? null,
        category_key: source.category_key ?? category?.$key ?? null,
        category,
    };

    for (const field of options.extraFields || []) {
        if (source[field] !== undefined) snapshot[field] = source[field];
    }

    return snapshot;
}

/** Linea con su copia de material reducida; conserva la unidad de su propia copia. */
export function withSlimMaterial<T extends { material?: any }>(line: T, options: { extraFields?: string[] } = {}): T {
    if (!line?.material || typeof line.material !== 'object') return line;
    return { ...line, material: slimMaterialSnapshot(line.material, { frozenFrom: line.material, ...options }) };
}

/** Linea de receta (semielaborado / produccion) con copias reducidas del material y de sus sustitutos. */
export function withSlimLine(line: any): any {
    const next: any = withSlimMaterial(line);
    if (Array.isArray(next?.substitutions)) {
        return {
            ...next,
            substitutions: next.substitutions.map((s: any) =>
                s && typeof s === 'object' ? slimMaterialSnapshot(s, { frozenFrom: s, extraFields: ['variable'] }) : s),
        };
    }
    return next;
}

/**
 * Semielaborado copiado a una produccion u orden de produccion: los campos de nivel superior se
 * leen en produccion y se conservan; solo se reducen las copias anidadas de sus lineas y salidas.
 */
export function withSlimNestedLines(semiFinished: any): any {
    if (!semiFinished || typeof semiFinished !== 'object') return semiFinished;
    return {
        ...semiFinished,
        ...(Array.isArray(semiFinished.recipe) ? { recipe: semiFinished.recipe.map(withSlimLine) } : {}),
        ...(Array.isArray(semiFinished.outputs) ? { outputs: semiFinished.outputs.map((o: any) => withSlimMaterial(o)) } : {}),
    };
}
